import React, { useEffect, useRef, useSyncExternalStore } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
// --- external imports ------

import AuthLoadingScreen from '~/authentication/AuthLoadingScreen';
import {
	getLinkActionRevision,
	hasUnspentLinkAction,
	subscribeToLinkActions
} from '~/authentication/lib/pendingLinkAction';
import { useUserContext } from '~/authentication/UserProvider';
// --- internal imports ------

function AuthRoute(props) {
	const user = useUserContext();

	const {
		component: Component,
		children,
		path,
		hasLinkActions,
	} = props;

	return (
		<RenderRoute
			children={children}
			Component={Component}
			path={path}
			user={user}
			hasLinkActions={hasLinkActions}
		/>
	);
}

function RenderRoute(props) {
	const { Component, children, user, hasLinkActions } = props;

	const hasRenderedScreenRef = useRef(false);
	const location = useLocation();

	// A signed-in player has nothing to do on an auth screen, with one exception:
	// the URL carries a single-use code they have just been sent. That code is a
	// request to change the session, so it outranks the one already here — and
	// redirecting on the strength of a live session would throw it away unspent.
	// See `pendingLinkAction` for why this asks whether the code has been *spent*
	// rather than whether it exists.
	//
	// Only the route that can actually spend one may be held open by it. Read off
	// the query string alone, a hand-written `/login?oobCode=...` kept a
	// signed-in visitor on the login form, which has no code to exchange and so
	// nothing that would ever release the redirect.
	//
	// Subscribed rather than merely read: the gate is released when the exchange
	// settles, and neither the URL nor the session need change at that moment —
	// a refused code changes nothing at all. Without a subscription there would
	// be no render in which to notice.
	useSyncExternalStore(subscribeToLinkActions, getLinkActionRevision);

	const shouldWaitForLinkAction = !!hasLinkActions && hasUnspentLinkAction(location.search);

	const shouldRedirectToGame = !shouldWaitForLinkAction
		&& !!(user?.firebaseUser && user?.tradersUserData);
	// Only the *initial* session check may replace the screen. Once the screen is
	// on display, an auth check that starts afterwards (the user submitted the
	// login form, and `UserProvider` is now logging the player in) must not
	// unmount it: the screen owns the loading state and the error message of the
	// request still in flight, and both are lost if it is torn down and remounted.
	const shouldWaitForAuthData = !!user?.isCheckingInitialAuthData && !hasRenderedScreenRef.current;

	useEffect(function latchRenderedScreen() {
		if (!shouldWaitForAuthData && !shouldRedirectToGame) {
			hasRenderedScreenRef.current = true;
		}
	}, [shouldWaitForAuthData, shouldRedirectToGame]);

	if (shouldWaitForAuthData) {
		return <AuthLoadingScreen />;
	}

	if (shouldRedirectToGame) {
		return <Navigate to="/game/" replace />;
	}

	return children || <Component />;
}

export default AuthRoute;
